import { useEffect, useState, useRef } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { THEME_COLOR } from "../config/club";

export default function JoinerPendingMatch({
  clubId,
  playerId,
  alertEnabled,
  sessionId,
  pendingOrder,
  setLastUpdate
}) {
  const [pendingMatch, setPendingMatch] = useState(null);
  const notifiedRef = useRef(false);

  const THEME_VIOLET = THEME_COLOR;

  const getCourtOrderLabel = (order) => {
    if (order === 1) return "Next available court";

    const suffix =
      order % 10 === 1 && order % 100 !== 11
        ? "st"
        : order % 10 === 2 && order % 100 !== 12
        ? "nd"
        : order % 10 === 3 && order % 100 !== 13
        ? "rd"
        : "th";

    return `${order}${suffix} available court`;
  };

  useEffect(() => {
    if (!clubId || !playerId) return;

    const q = query(
      collection(db, "clubs", clubId, "sessions", sessionId, "matches"),
      where("status", "==", "pending")
    );

    const unsub = onSnapshot(q, (snap) => {
      let found = null;
      snap.forEach((doc) => {
        const data = doc.data();
        if (data.players?.some((p) => p.id === playerId)) {
          found = { id: doc.id, ...data };
        }
      });
      setPendingMatch(found);
      setLastUpdate(Date.now());
    });

    return () => unsub();
  }, [clubId, playerId, sessionId]);

  useEffect(() => {
    if (!pendingMatch || !alertEnabled) {
      notifiedRef.current = false;
      return;
    }
    if (notifiedRef.current) return;

    notifiedRef.current = true;

    const u = new SpeechSynthesisUtterance(
      "Get ready. You are next to play."
    );
    u.rate = 0.95;
    window.speechSynthesis.speak(u);

    if (navigator.vibrate) navigator.vibrate([300, 200, 300]);
  }, [pendingMatch, alertEnabled]);

  if (!pendingMatch) return null;

  const teamA = pendingMatch.players.slice(0, 2);
  const teamB = pendingMatch.players.slice(2, 4);

  return (
    <div className="mt-4 px-2">
      <h6
        className="fw-bold mb-3 text-uppercase small tracking-widest"
        style={{ color: THEME_VIOLET }}
      >
        <span
          className="spinner-grow spinner-grow-sm me-2"
          style={{ backgroundColor: THEME_VIOLET }}
        ></span>
        Up Next
      </h6>

      <div
        className="card border-0 shadow-lg rounded-4 overflow-hidden mb-4 text-white"
        style={{
          background: `linear-gradient(
            135deg,
            ${THEME_VIOLET} 0%,
            color-mix(in srgb, ${THEME_VIOLET} 70%, #000) 100%
          )`
        }}
      >
        <div className="card-body p-4">
          <div className="text-center mb-4">
            <div
              className="badge rounded-pill mb-2 px-3"
              style={{
                backgroundColor: "rgba(255,255,255,0.2)",
                border: "1px solid rgba(255,255,255,0.3)"
              }}
            >
              WAITING FOR COURT
            </div>

            <h3 className="fw-black mb-0">Get Ready!</h3>

            <p className="small opacity-75">
              Queue position:
              <br />
              <strong style={{ fontSize: 20 }}>
                {getCourtOrderLabel(pendingOrder)}
              </strong>
            </p>

            <p className="small opacity-75">
              Your match is being prepared
            </p>
          </div>

          <div className="vstack gap-3">
            {/* TEAM A */}
            <div
              className="p-3 rounded-4"
              style={{
                backgroundColor: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.1)"
              }}
            >
              <div className="text-center">
                <div
                  className="small opacity-50 fw-black tracking-widest mb-3"
                  style={{ fontSize: "0.6rem" }}
                >
                  TEAM A
                </div>

                <div className="d-flex justify-content-center mb-2">
                  {teamA.map((p, index) => (
                    <div
                      key={p.id}
                      className="rounded-circle overflow-hidden shadow-sm"
                      style={{
                        width: 45,
                        height: 45,
                        marginLeft: index === 0 ? 0 : -15,
                        zIndex: 2 - index,
                        border: "2px solid rgba(255,255,255,0.4)"
                      }}
                    >
                      {p.photoURL ? (
                        <img
                          src={p.photoURL}
                          alt={p.name}
                          className="w-100 h-100 object-fit-cover"
                        />
                      ) : (
                        <div className="w-100 h-100 d-flex align-items-center justify-content-center text-white-50">
                          <i className="bi bi-person-fill"></i>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="fw-bold fs-6 mt-1">
                  {teamA.map((p) => p.name).join(" & ")}
                </div>
              </div>
            </div>

            {/* VS */}
            <div className="d-flex align-items-center justify-content-center gap-2">
              <hr className="flex-grow-1 opacity-25" />
              <span className="small fw-black opacity-25">VS</span>
              <hr className="flex-grow-1 opacity-25" />
            </div>

            {/* TEAM B */}
            <div
              className="p-3 rounded-4"
              style={{
                backgroundColor: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.1)"
              }}
            >
              <div className="text-center">
                <div
                  className="small opacity-50 fw-black tracking-widest mb-3"
                  style={{ fontSize: "0.6rem" }}
                >
                  TEAM B
                </div>

                <div className="d-flex justify-content-center mb-2">
                  {teamB.map((p, index) => (
                    <div
                      key={p.id}
                      className="rounded-circle overflow-hidden shadow-sm"
                      style={{
                        width: 45,
                        height: 45,
                        marginLeft: index === 0 ? 0 : -15,
                        zIndex: 2 - index,
                        border: "2px solid rgba(255,255,255,0.4)"
                      }}
                    >
                      {p.photoURL ? (
                        <img
                          src={p.photoURL}
                          alt={p.name}
                          className="w-100 h-100 object-fit-cover"
                        />
                      ) : (
                        <div className="w-100 h-100 d-flex align-items-center justify-content-center text-white-50">
                          <i className="bi bi-person-fill"></i>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="fw-bold fs-6 mt-1">
                  {teamB.map((p) => p.name).join(" & ")}
                </div>
              </div>
            </div>
          </div>

          <div
            className="mt-4 pt-2 text-center"
            style={{ borderTop: "1px solid rgba(255,255,255,0.15)" }}
          >
            <small className="opacity-75">
              <i className="bi bi-volume-up-fill me-2"></i>
              Voice alerts enabled
            </small>
          </div>
        </div>
      </div>
    </div>
  );
}